import React, { useState } from "react";
import clsx from "clsx";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { Overview } from "@app/components/overview/overview";
import { ROUTES } from "@app/constants/routes";
import { SyncRunResultDTO } from "@app/api/generated";

import { runSync, setPlatformEnabled } from "./product-page-sync-api";
import { ConfirmModal } from "@app/components/confirm-modal/confirm-modal";
import { PlatformSidebar } from "./components/platform-sidebar";
import { RunsTable } from "./components/runs-table";
import { SyncFilterBar } from "./components/sync-filter-bar";
import { getPlatformName } from "@app/util/platform-name";
import { useSyncDashboard } from "./use-sync-dashboard";
import styles from "@app/styles/dashboard/dashboard-layout.module.scss";

interface IPendingAction {
  title: string;
  message: string;
  run: () => Promise<void>;
}

const ProductPageSync = (): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    platforms,
    runs,
    isLoading,
    currentPage,
    total,
    totalPages,
    statusFilter,
    platformFilter,
    anyRunning,
    refresh,
    setPlatforms,
    setCurrentPage,
    setStatusFilter,
    setPlatformFilter
  } = useSyncDashboard();

  const [pendingAction, setPendingAction] = useState<IPendingAction | null>(null);

  const markRunning = (predicate: (platformId: number) => boolean) => {
    setPlatforms((current) =>
      current.map((platform) => (predicate(platform.platformId) ? { ...platform, isRunning: true } : platform))
    );
  };

  // --- The actual mutations, run only after the user confirms in the modal. ---

  const doToggleEnabled = async (platformId: number, enabled: boolean) => {
    // Optimistic: flip the state immediately, reconcile with the server's row on success.
    setPlatforms((current) =>
      current.map((platform) =>
        platform.platformId === platformId ? { ...platform, productPageSyncEnabled: enabled } : platform
      )
    );
    try {
      const updated = await setPlatformEnabled(platformId, enabled);
      setPlatforms((current) => current.map((platform) => (platform.platformId === platformId ? updated : platform)));
    } catch {
      toast.error(intl.formatMessage({ id: "productPageSync.toast.toggleError" }));
      await refresh();
    }
  };

  const doSyncPlatform = async (platformId: number) => {
    markRunning((id) => id === platformId);
    try {
      await runSync(platformId);
      toast.success(intl.formatMessage({ id: "productPageSync.toast.syncStarted" }));
    } catch {
      toast.error(intl.formatMessage({ id: "productPageSync.toast.syncError" }));
    } finally {
      await refresh();
    }
  };

  const doSyncAll = async () => {
    markRunning((id) => platforms.some((p) => p.platformId === id && p.productPageSyncEnabled));
    try {
      await runSync();
      toast.success(intl.formatMessage({ id: "productPageSync.toast.syncStarted" }));
    } catch {
      toast.error(intl.formatMessage({ id: "productPageSync.toast.syncError" }));
    } finally {
      await refresh();
    }
  };

  const requestSyncAll = () => {
    setPendingAction({
      title: intl.formatMessage({ id: "productPageSync.confirm.syncAll.title" }),
      message: intl.formatMessage({ id: "productPageSync.confirm.syncAll.message" }),
      run: doSyncAll
    });
  };

  const requestSyncPlatform = (platformId: number) => {
    setPendingAction({
      title: intl.formatMessage({ id: "productPageSync.confirm.sync.title" }),
      message: intl.formatMessage(
        { id: "productPageSync.confirm.sync.message" },
        { platform: getPlatformName(intl, platformId) }
      ),
      run: () => doSyncPlatform(platformId)
    });
  };

  const requestToggleEnabled = (platformId: number, enabled: boolean) => {
    setPendingAction({
      title: intl.formatMessage({
        id: enabled ? "productPageSync.confirm.activate.title" : "productPageSync.confirm.deactivate.title"
      }),
      message: intl.formatMessage(
        { id: enabled ? "productPageSync.confirm.activate.message" : "productPageSync.confirm.deactivate.message" },
        { platform: getPlatformName(intl, platformId) }
      ),
      run: () => doToggleEnabled(platformId, enabled)
    });
  };

  const handleConfirm = async () => {
    if (!pendingAction) {
      return;
    }
    const { run } = pendingAction;
    // Close the modal immediately; feedback comes via optimistic RUNNING state, polling and toasts.
    setPendingAction(null);
    await run();
  };

  return (
    <div>
      <div className={clsx(styles.layout, styles.container)}>
        <Overview
          noContainer
          className={styles.tableColumn}
          title={intl.formatMessage({ id: "productPageSync.title" }, { count: total })}
          actionBar={
            <SyncFilterBar
              platforms={platforms}
              statusFilter={statusFilter}
              platformFilter={platformFilter}
              onStatusChange={setStatusFilter}
              onPlatformChange={setPlatformFilter}
            />
          }
          isLoading={isLoading}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          overviewItems={runs}
        >
          <RunsTable
            runs={runs}
            isLoading={isLoading}
            onRowClick={(run: SyncRunResultDTO) => navigate(`${ROUTES.PRODUCT_PAGE_SYNC}/${run.runId}`)}
          />
        </Overview>

        <div className={styles.sidebar}>
          <PlatformSidebar
            platforms={platforms}
            anyRunning={anyRunning}
            onSyncAll={requestSyncAll}
            onSyncPlatform={requestSyncPlatform}
            onToggleEnabled={requestToggleEnabled}
          />
        </div>
      </div>

      <ConfirmModal
        isOpen={pendingAction !== null}
        title={pendingAction?.title ?? ""}
        message={pendingAction?.message ?? ""}
        onConfirm={handleConfirm}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
};

export { ProductPageSync };
